/**
 * Parses the `form_responses` payload returned by GET /jobs/data-capture/:jobId
 * into a per-form, per-section structure for display.
 *
 * The backend returns one row per `job_task_form` submission, each carrying its
 * own `form_name` and a `form_data` JSON blob whose sections carry a
 * `section_name`. Flattening those rows into a single list loses the form
 * boundaries and misattributes captured photos between forms (WRK-5765).
 */

/** A single question/answer pair, plus any photos captured against it. */
export interface ParsedFormAnswer {
  question: string;
  answer: string;
  /** Resolved presigned image URLs from an `s3_image` or `signature` control. Empty for other controls. */
  images: string[];
  /**
   * True when this answer came from a conditional branch rather than a question of
   * its own, so the panel can indent it under the question that revealed it.
   */
  nested?: boolean;
}

/** One section of a submitted form. */
export interface ParsedFormSection {
  /** `section_name` from the stored form_data. Empty when the form has none. */
  sectionName: string;
  answers: ParsedFormAnswer[];
}

/** One submitted form (one `job_task_form` row). */
export interface ParsedFormGroup {
  /** Stable key — the `job_task_form` row id, so repeat submissions stay distinct. */
  key: string;
  formName: string;
  submittedAt: string | null;
  sections: ParsedFormSection[];
}

/** The subset of the API row this parser reads. */
export interface FormResponseRow {
  id: number;
  form_name?: string | null;
  form_data?: unknown;
  created_time?: string | null;
}

const GENERIC_FORM_LABEL = 'Form';

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : null;
}

function trimmedOrNull(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Resolve the heading for a submitted form. The `form` table join is reliable in
 * practice, but fall back through the embedded form_data name to a generic label
 * so a panel is never unlabelled.
 */
function resolveFormName(row: FormResponseRow, formData: Record<string, unknown>): string {
  return trimmedOrNull(row.form_name) ?? trimmedOrNull(formData.name) ?? GENERIC_FORM_LABEL;
}

/**
 * Normalise the many shapes `form_data` takes in the wild:
 * - a form object with a `sections` array (what the mobile app submits)
 * - a bare array of sections
 * - a single section object carrying `controls`
 * - a flat `{ question, answer }` object (legacy)
 */
function normaliseSections(formData: Record<string, unknown>): unknown[] {
  if (Array.isArray(formData)) {
    return formData;
  }
  return Array.isArray(formData.sections) ? formData.sections : [formData];
}

/**
 * Turn one control into an answer row, or null when it carries nothing to show.
 */
function parseControl(control: Record<string, unknown>): ParsedFormAnswer | null {
  const question = String(control.control_name ?? '');
  const controlType = String(control.control_type ?? '');
  const value = control.value;

  // Label-only controls are presentational headings, not answers.
  if (controlType === 'label') {
    return null;
  }

  if ((controlType === 's3_image' || controlType === 'signature') && value != null) {
    const items = Array.isArray(value) ? value : [value];
    const images: string[] = [];
    let capturedCount = 0;
    for (const entry of items) {
      const img = asRecord(entry);
      if (!img) {
        continue;
      }
      capturedCount += 1;
      if (typeof img.url === 'string') {
        images.push(img.url);
      }
    }
    // A captured item with no resolved url (presign failed, or legacy data) must
    // still be counted — reporting "0 photos captured" on a compliance form reads
    // as "no photo taken" rather than "photo failed to load" (CWL-301 review).
    const unavailable = capturedCount - images.length;
    const answer =
      unavailable > 0
        ? `${capturedCount} photo${capturedCount === 1 ? '' : 's'} captured – ${unavailable} unavailable`
        : `${capturedCount} photo${capturedCount === 1 ? '' : 's'} captured`;
    return { question, answer, images };
  }

  if (controlType === 'map' && Array.isArray(value)) {
    const coord = asRecord(value[0]);
    if (!coord) {
      return null;
    }
    return {
      question,
      answer: `${Number(coord.latitude ?? 0).toFixed(6)}, ${Number(coord.longitude ?? 0).toFixed(6)}`,
      images: [],
    };
  }

  if (Array.isArray(value)) {
    return {
      question,
      answer: value.map((v) => (typeof v === 'object' ? JSON.stringify(v) : String(v))).join(', '),
      images: [],
    };
  }

  if (value != null) {
    return { question, answer: String(value), images: [] };
  }

  return null;
}

/**
 * A conditional_field stores the crew's answer as the chosen condition's name, and
 * the fields they then filled in live under that condition's `conditionalControls`.
 * Reading only the control's own `value` therefore loses every branch answer, even
 * though the mobile app submits them (SC-6511). Only the chosen branch is expanded:
 * the other branches keep whatever values a previous answer left behind, and showing
 * those would misreport what was captured.
 */
function parseBranchAnswers(control: Record<string, unknown>): ParsedFormAnswer[] {
  if (String(control.control_type ?? '') !== 'conditional_field') {
    return [];
  }
  const chosen = Array.isArray(control.value) ? control.value[0] : control.value;
  if (chosen == null || !Array.isArray(control.conditions)) {
    return [];
  }

  const answers: ParsedFormAnswer[] = [];
  for (const condition of control.conditions) {
    const cond = asRecord(condition);
    if (!cond || String(cond.condition_name ?? '') !== String(chosen)) {
      continue;
    }
    const branchControls = Array.isArray(cond.conditionalControls) ? cond.conditionalControls : [];
    for (const branchControl of branchControls) {
      const branch = asRecord(branchControl);
      if (!branch) {
        continue;
      }
      // Reuse parseControl so s3_image, map and array values format identically to
      // a top-level field of the same type.
      const parsed = parseControl(branch);
      if (parsed) {
        answers.push({ ...parsed, nested: true });
      }
    }
  }
  return answers;
}

/** Structural equality over JSON-like control data (object key order is irrelevant; array order is not). */
function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((v, i) => deepEqual(v, b[i]));
  }
  if (Array.isArray(a) || Array.isArray(b)) return false;
  const recordA = asRecord(a);
  const recordB = asRecord(b);
  if (recordA && recordB) {
    const keysA = Object.keys(recordA);
    const keysB = Object.keys(recordB);
    return (
      keysA.length === keysB.length &&
      keysA.every((key) => key in recordB && deepEqual(recordA[key], recordB[key]))
    );
  }
  return false;
}

/**
 * One control-array per repeat copy, in order. Mobile's `getSubmitForm`
 * (`DynamicFormComponent.tsx` / `DynamicLayoutFormComponent.tsx`) unconditionally
 * appends the live `controls` value onto `repeated_sections` on submit, without
 * clearing `controls` — so `repeated_sections`'s last entry and `controls` are
 * always the SAME submitted copy by construction, never two independently
 * filled copies that happen to match (observed on CWL-323 staging job
 * 001008658528 — SAP received the "5th" copy twice, as two CurrentSSRAs items
 * with different MobileKeys). Deep-equality here is safe precisely because of
 * that guarantee: two genuinely distinct copies with identical answers never
 * reach this check, since the dedupe only ever compares the last entry against
 * the one value mobile is guaranteed to have duplicated.
 */
function instanceControls(sec: Record<string, unknown>): unknown[] {
  const repeated = Array.isArray(sec.repeated_sections) ? sec.repeated_sections : [];
  const base = Array.isArray(sec.controls) ? sec.controls : null;
  if (!base) return repeated;
  const last = repeated[repeated.length - 1];
  if (last !== undefined && deepEqual(last, base)) return repeated;
  return [...repeated, base];
}

function parseSection(section: unknown): ParsedFormSection[] {
  const sec = asRecord(section);
  if (!sec) return [];

  const sectionName = trimmedOrNull(sec.section_name) ?? '';

  if (Array.isArray(sec.controls) || Array.isArray(sec.repeated_sections)) {
    return instanceControls(sec)
      .filter((controls): controls is unknown[] => Array.isArray(controls))
      .map((controls) => {
        const answers: ParsedFormAnswer[] = [];
        for (const control of controls) {
          const ctrl = asRecord(control);
          if (!ctrl) continue;
          const answer = parseControl(ctrl);
          if (answer) answers.push(answer);
          // Branch answers follow their parent question, so the panel reads in the order
          // the crew filled it in.
          answers.push(...parseBranchAnswers(ctrl));
        }
        return { sectionName, answers };
      });
  }

  // Legacy flat shape: a single { question, answer } object.
  if ('question' in sec) {
    return [
      {
        sectionName,
        answers: [
          { question: String(sec.question ?? ''), answer: String(sec.answer ?? ''), images: [] },
        ],
      },
    ];
  }

  return [];
}

/**
 * Group the API's flat `form_responses` array into one entry per submitted form,
 * ordered by submission time (the order the crew actually completed them in).
 * Rows whose `form_data` is absent or non-object are skipped — the DB holds many
 * such placeholder rows for forms that were assigned but never filled in.
 */
export function parseFormResponses(rows: readonly FormResponseRow[]): ParsedFormGroup[] {
  const groups: ParsedFormGroup[] = [];

  for (const row of rows) {
    const formData = asRecord(row.form_data);
    if (!formData) {
      continue;
    }

    const sections: ParsedFormSection[] = [];
    for (const section of normaliseSections(formData)) {
      sections.push(...parseSection(section));
    }

    groups.push({
      key: String(row.id),
      formName: resolveFormName(row, formData),
      submittedAt: row.created_time ?? null,
      sections,
    });
  }

  // Ascending by submission time; rows without a timestamp keep their original
  // relative order at the end. Index tiebreak keeps the sort stable.
  return groups
    .map((group, index) => ({ group, index }))
    .sort((a, b) => {
      const aTime = a.group.submittedAt ? Date.parse(a.group.submittedAt) : Number.NaN;
      const bTime = b.group.submittedAt ? Date.parse(b.group.submittedAt) : Number.NaN;
      const aValid = !Number.isNaN(aTime);
      const bValid = !Number.isNaN(bTime);

      if (aValid && bValid && aTime !== bTime) {
        return aTime - bTime;
      }
      if (aValid !== bValid) {
        return aValid ? -1 : 1;
      }
      return a.index - b.index;
    })
    .map(({ group }) => group);
}

/**
 * Total answer count across a form's sections — used to decide whether a panel
 * has anything worth showing.
 */
export function countAnswers(group: ParsedFormGroup): number {
  return group.sections.reduce((total, section) => total + section.answers.length, 0);
}
